/**
 * AudienceBehaviorSection — "Το κοινό σου"
 *
 * Shows turnover behavior (Πιστό / Φυσιολογικό / Περαστικό) + new-listeners-per-hour
 * estimate using peak/avg proxy (until real STREAMHITS delta tracking is available).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Heart, Clock, Users, TrendingUp, AlertCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  '♡': Heart,
  '○': Users,
  '↻': TrendingUp,
  '⏱': Clock,
};

const BEHAVIOR_COLOR = {
  'Πιστό':        { bg: 'bg-rose-500/10',    border: 'border-rose-500/30',    text: 'text-rose-300'    },
  'Φυσιολογικό':  { bg: 'bg-blue-500/10',    border: 'border-blue-500/30',    text: 'text-blue-300'    },
  'Περαστικό':    { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-300'   },
};

const AudienceBehaviorSection = ({ stationId, token, dateFrom, dateTo, daypart }) => {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!stationId || !token) return;
    setLoading(true);
    try {
      const q = new URLSearchParams({
        date_from: dateFrom, date_to: dateTo, daypart: daypart || 'all',
      });
      const r = await fetch(`${API}/api/songs-analyzer/audience-behavior/${stationId}?${q}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) setData(await r.json());
      else setData(null);
    } finally { setLoading(false); }
  }, [stationId, token, dateFrom, dateTo, daypart]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-white/40" data-testid="audience-behavior-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Φόρτωση...
      </div>
    );
  }

  if (!data || !data.has_data) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-lg p-6 text-center" data-testid="audience-behavior-empty">
        <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400/60" />
        <div className="text-white font-medium mb-1">Δεν έχουμε στοιχεία ακόμα</div>
        <div className="text-sm text-white/50">
          {data?.empty_message || t('i18nB3_audience_unavailable')}
        </div>
      </div>
    );
  }

  const beh = data.behavior || {};
  const color = BEHAVIOR_COLOR[beh.label] || BEHAVIOR_COLOR['Φυσιολογικό'];
  const Icon = ICONS[beh.icon] || Users;
  const isExact = data.accuracy === 'exact';
  const AccuracyBadge = isExact ? (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-medium"
      title={t('i18nB3_audience_exact')}
      data-testid="accuracy-exact"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
      Ακριβές νούμερο
    </span>
  ) : (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-medium"
      title={t('i18nB3_audience_estimate')}
      data-testid="accuracy-estimate"
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
      Εκτίμηση
    </span>
  );

  return (
    <div className="space-y-4" data-testid="audience-behavior-section">
      {/* Main behavior card */}
      <div className={`${color.bg} ${color.border} border rounded-xl p-5`}>
        <div className="flex items-start gap-4">
          <div className={`w-14 h-14 rounded-xl ${color.bg} ${color.border} border flex items-center justify-center shrink-0`}>
            <Icon className={`w-7 h-7 ${color.text}`} />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-white/40 mb-1">Συμπεριφορά κοινού</div>
            <div className={`text-2xl font-bold ${color.text} mb-1`} data-testid="behavior-label">
              {beh.label}
            </div>
            <div className="text-sm text-white/70">{beh.description}</div>
          </div>
        </div>
      </div>

      {/* Secondary metrics — new listeners per hour + average listen time */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-blue-400" />
              <span className="text-xs uppercase text-white/50 font-semibold">Νέοι ακροατές την ώρα</span>
            </div>
            {AccuracyBadge}
          </div>
          {data.new_listeners_per_hour != null ? (
            <>
              <div className="text-3xl font-bold text-white" data-testid="new-listeners-value">
                {isExact ? data.new_listeners_per_hour : `~${data.new_listeners_per_hour}`}
              </div>
              <div className="text-xs text-white/40 mt-1">
                {isExact
                  ? `μέσος όρος από ${data.cume_hours_covered} ώρες ακριβούς καταγραφής`
                  : t('i18nB3_audience_renewal')}
              </div>
            </>
          ) : (
            <div className="text-sm text-white/40">Μη διαθέσιμο</div>
          )}
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-emerald-400" />
            <span className="text-xs uppercase text-white/50 font-semibold">Μέσος χρόνος ακρόασης / ανά συντονισμό</span>
          </div>
          {data.avg_listen_minutes_estimate ? (
            <>
              <div className="text-3xl font-bold text-white" data-testid="avg-listen-time">
                {data.avg_listen_minutes_estimate}
              </div>
              <div
                className="text-xs text-white/40 mt-1"
                title="Διάρκεια ενός μεμονωμένου session (κάθε φορά που ένας ακροατής συντονίζεται). Διαφέρει από το ASL / day, που είναι το αθροιστικό σύνολο μέσα στην ημέρα."
              >
                λεπτά ανά συντονισμό
              </div>
            </>
          ) : (
            <div className="text-sm text-white/40">Μη διαθέσιμο</div>
          )}
        </div>
      </div>

      {/* Reference: current peak/avg */}
      <div className="bg-white/[0.03] border border-white/5 rounded-lg px-4 py-3 text-xs text-white/50 flex flex-wrap gap-x-5 gap-y-1">
        <span>Κορυφή: <span className="text-white/80 font-mono">{data.peak}</span></span>
        <span>Μέσος όρος: <span className="text-white/80 font-mono">{data.avg}</span></span>
        <span>Ώρες με δεδομένα: <span className="text-white/80">{data.hours_with_data}</span></span>
      </div>
    </div>
  );
};

export default AudienceBehaviorSection;
